/**
 * CG Boost shared type definitions (frontend mirror).
 *
 * ⚠️ MUST STAY IN SYNC with backend/src/types/cgBoost.ts.
 *
 * CRA's webpack config restricts compilation to files inside frontend/src/,
 * which prevents a true single-source `shared/` package without ejecting.
 * Until that changes, these interfaces are intentionally duplicated here:
 * the canonical definitions live on the backend; this file mirrors only
 * the shapes the frontend needs (campaign + view payloads + create-request).
 *
 * Drift is enforced by the `CG Boost Type Parity` GitHub Actions workflow
 * (.github/workflows/cg-boost-type-parity.yml), which runs on every push /
 * PR touching either file. To run locally:
 *   npx ts-node backend/scripts/checkCgBoostTypeParity.ts
 */

export const CG_BOOST_PLATFORMS = ['tiktok', 'meta', 'youtube'] as const;
export type CgBoostPlatform = typeof CG_BOOST_PLATFORMS[number];

export const CG_BOOST_CAMPAIGN_STATUSES = [
  'planned',
  'active',
  'completed',
  'cancelled',
] as const;
export type CgBoostCampaignStatus = typeof CG_BOOST_CAMPAIGN_STATUSES[number];

export const CG_BOOST_ASSET_FORMATS = ['Reaction', 'Demo', 'Story', 'Tutorial'] as const;
export type CgBoostAssetFormat = typeof CG_BOOST_ASSET_FORMATS[number];

/**
 * Where this asset row came from and what its current curation state is.
 *   `auto`      — pipeline auto-discovered it during a daily pull, awaiting
 *                 human review. Counts in totals; rendered with a "new" badge.
 *   `confirmed` — a human reviewed and accepted the asset.
 *   `hidden`    — a human hid the asset (e.g. wrong campaign linkage). Stays
 *                 in Firestore for audit but its metrics are excluded from
 *                 every aggregation in the dashboard.
 */
export const CG_BOOST_ASSET_DISCOVERY_SOURCES = ['auto', 'confirmed', 'hidden'] as const;
export type CgBoostAssetDiscoverySource = typeof CG_BOOST_ASSET_DISCOVERY_SOURCES[number];

/**
 * Campaign-level rollup of which intended platforms have valid platform IDs
 * registered. Derived on read from `plannedBudget` (intent) + `platformIds`
 * (reality), never stored — so changing the budget or linking a platform
 * immediately reflects without a backfill.
 */
export const CG_BOOST_LINK_STATUSES = ['unlinked', 'partial', 'linked'] as const;
export type CgBoostLinkStatus = typeof CG_BOOST_LINK_STATUSES[number];

/**
 * Per-platform pull health, also derived on read. Combines
 * `cgBoost_pullStatus` (last attempt / success / error) with the campaign's
 * platformIds presence so the dashboard can render one of five badge states
 * without a second round-trip.
 */
export const CG_BOOST_PLATFORM_HEALTHS = [
  'not_linked',
  'linked_no_data',
  'healthy',
  'stale',
  'erroring',
] as const;
export type CgBoostPlatformHealth = typeof CG_BOOST_PLATFORM_HEALTHS[number];

export interface CgBoostPullStatus {
  canonicalCampaignId: string;
  platform: CgBoostPlatform;
  /** Most recent pull attempt — success or failure. */
  lastAttemptAt: string | null;
  /** Most recent successful pull. Null until the first success. */
  lastSuccessAt: string | null;
  /** Rows ingested in the most recent successful pull. */
  rowsLastPull: number;
  /** Most recent error message, if any. Cleared on next success. */
  lastError: string | null;
  lastErrorAt: string | null;
  health: CgBoostPlatformHealth;
}

/**
 * Resolved campaign details surfaced to the linking wizard's dry-run preview.
 * Every field is optional so partial responses still render usefully.
 */
export interface CgBoostResolvedCampaign {
  campaignName?: string;
  advertiserName?: string;
  /** Platform-native status string (e.g. 'ENABLED', 'ACTIVE', 'PAUSED'). */
  status?: string;
  startDate?: string;
  endDate?: string;
  /** ISO 4217 (e.g. 'USD'). */
  currency?: string;
  /** IANA timezone (e.g. 'America/New_York'). */
  timezone?: string;
}

/**
 * Discriminated union returned by `POST /platform-links/validate` and by each
 * integration's `validateCampaign()`. `validated: false` means we couldn't
 * actually check (creds missing) — wizard treats it as "proceed with caution",
 * not "user error".
 */
export type CgBoostValidateLinkResult =
  | { validated: false; reason: string }
  | {
      validated: true;
      ok: boolean;
      resolved?: CgBoostResolvedCampaign;
      errors: string[];
      warnings: string[];
    };

/**
 * Source of a change-log entry. `ui` = a human edit through the dashboard,
 * `pipeline` = the daily-pull writing assets / metrics / signals,
 * `admin` = a backend script or admin tool.
 */
export const CG_BOOST_CHANGE_SOURCES = ['ui', 'pipeline', 'admin'] as const;
export type CgBoostChangeSource = typeof CG_BOOST_CHANGE_SOURCES[number];

/**
 * One entry in a campaign or asset's change log. Field paths use dot-notation
 * (e.g. `platformIds.youtube`, `plannedBudget.tiktok`, `format`) so the UI
 * can group / filter consistently across both subjects.
 *
 * `before` / `after` are JSON-serialisable. They may be primitives, arrays,
 * or objects depending on what changed; the UI renders them as JSON for
 * non-primitives and inline for scalars.
 */
export interface CgBoostChangeLogEntry {
  changeId: string;
  /** ISO 8601 timestamp. */
  at: string;
  byUid: string;
  byEmail: string;
  /** Dotted field path of what changed. `__created` for the initial insert. */
  field: string;
  before: unknown;
  after: unknown;
  source: CgBoostChangeSource;
}

export interface CgBoostMetricBag {
  impressions: number;
  views: number;
  clicks: number;
  spend: number;
  threeSecondViews: number;
  completedViews: number;
  vtr: number;
  hookRate: number;
  completionRate: number;
  ctr: number;
  cpm: number;
  cpv: number;
  frequency: number;
}

/**
 * Static provenance map for every field in `CgBoostMetricBag`. Records the
 * platform-native source for each metric so the dashboard can render an ⓘ
 * tooltip explaining what a number actually represents.
 *
 * NOTE: parity with the backend is NOT enforced by checkCgBoostTypeParity
 * (which only looks at interface / type aliases). Keep both copies in
 * lockstep manually — drift here means the tooltips lie.
 */
export type CgBoostMetricKey = keyof CgBoostMetricBag;

export const CG_BOOST_METRIC_PROVENANCE: Record<
  CgBoostMetricKey,
  Partial<Record<CgBoostPlatform | 'derived', string>>
> = {
  impressions: {
    tiktok: 'impressions (native)',
    meta: 'impressions',
    youtube: 'metrics.impressions',
  },
  views: {
    tiktok: 'video_play_actions (play starts; includes <3s plays + loops)',
    meta: 'video_play_actions (play starts; includes <3s plays + loops)',
    youtube: 'metrics.video_views (true views — ≥30s or full play)',
  },
  clicks: {
    tiktok: 'clicks',
    meta: 'clicks',
    youtube: 'metrics.clicks',
  },
  spend: {
    tiktok: 'spend (account currency)',
    meta: 'spend (account currency)',
    youtube: 'metrics.cost_micros / 1,000,000',
  },
  threeSecondViews: {
    tiktok: 'video_watched_2s (2s proxy — no native 3s metric)',
    meta: 'video_thruplay_watched_actions (15s+ or full play)',
    youtube: 'video_quartile_p25_rate × impressions',
  },
  completedViews: {
    tiktok: 'video_views_p100 (100% quartile)',
    meta: 'video_p100_watched_actions (100% quartile)',
    youtube: 'video_quartile_p100_rate × impressions',
  },
  frequency: {
    tiktok: 'frequency (adgroup-level)',
    meta: 'frequency (per-ad)',
    youtube: 'metrics.average_frequency (account-period)',
  },
  vtr: { derived: 'views / impressions' },
  hookRate: { derived: 'threeSecondViews / impressions' },
  completionRate: { derived: 'completedViews / impressions' },
  ctr: { derived: 'clicks / impressions' },
  cpm: { derived: 'spend / (impressions / 1000)' },
  cpv: { derived: 'spend / views' },
};

/**
 * Subset of metrics whose underlying source semantics differ between
 * platforms — summing or directly comparing across platforms is misleading.
 * Used by Snapshot totals to show a footnote warning.
 */
export const CG_BOOST_NON_COMPARABLE_METRICS: ReadonlyArray<CgBoostMetricKey> = [
  'views',
  'threeSecondViews',
  'hookRate',
  'completionRate',
  'cpv',
  'vtr',
  'frequency',
];

export interface CgBoostCreator {
  airtableRecordId?: string;
  name: string;
  slug: string;
}

export interface CgBoostPlannedBudget {
  tiktok: number;
  meta: number;
  youtube: number;
  total: number;
}

export interface CgBoostPlatformIds {
  tiktok?: { campaignId: string; advertiserId: string };
  meta?: { campaignId: string; adAccountId: string };
  // Google Ads (advertiser-side) Video / Shorts campaigns. customerId is the
  // brand's 10-digit Google Ads customer ID; loginCustomerId optionally
  // overrides the env GOOGLE_ADS_LOGIN_CUSTOMER_ID (CG's manager / MCC) when
  // a campaign lives under a different MCC.
  youtube?: { customerId: string; campaignId: string; loginCustomerId?: string };
}

export interface CgBoostCampaign {
  canonicalCampaignId: string;
  brandName: string;
  brandSlug: string;
  campaignName?: string;
  industryVertical: string;
  startDate: string;
  endDate: string;
  status: CgBoostCampaignStatus;
  firstLineOfCopy: string;
  copyHash: string;
  creators: CgBoostCreator[];
  plannedBudget: CgBoostPlannedBudget;
  platformIds: CgBoostPlatformIds;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  finalReportGenerated: boolean;
  finalReportDriveFileId?: string;
  /** Derived on read from plannedBudget + platformIds. Never stored. */
  linkStatus: CgBoostLinkStatus;
}

export interface SnapshotPayload {
  campaign: CgBoostCampaign;
  productionSummary: { creatorCount: number; assetCount: number; platforms: CgBoostPlatform[] };
  totals: CgBoostMetricBag;
  budgetDeployment: { spend: number; plannedTotal: number; pctDeployed: number };
}

export interface PlatformBreakdownEntry {
  platform: CgBoostPlatform;
  shareOfReachPct: number;
  metrics: CgBoostMetricBag;
  spendPacing: { spend: number; planned: number; pctDeployed: number };
}

export interface PlatformsPayload {
  campaign: CgBoostCampaign;
  perPlatform: PlatformBreakdownEntry[];
  flags: {
    topAttention: CgBoostPlatform | null;
    mostEfficient: CgBoostPlatform | null;
    strongestResponse: CgBoostPlatform | null;
  };
}

export interface CreativeRow {
  canonicalAssetId: string;
  platform: CgBoostPlatform;
  creatorName: string;
  format: CgBoostAssetFormat;
  hookStyle: string;
  metrics: CgBoostMetricBag;
  contentResonanceScore: number;
  rank: number;
  flags: { topAsset: boolean; hookStrength: boolean };
  discoverySource: CgBoostAssetDiscoverySource;
  creativeGroupId?: string;
}

export interface CreativeGroupRow {
  creativeGroupId: string;
  label: string;
  creatorName: string;
  format: CgBoostAssetFormat | 'Multi-format';
  hookStyle: string;
  platforms: CgBoostPlatform[];
  metrics: CgBoostMetricBag;
  contentResonanceScore: number;
  rank: number;
  crossPlatform: boolean;
  children: CreativeRow[];
}

export interface CreativePayload {
  campaign: CgBoostCampaign;
  rows: CreativeRow[];
  groups: CreativeGroupRow[];
}

export interface CreativeGroupSuggestion {
  suggestionId: string;
  candidateAssetIds: string[];
  creatorName: string;
  format: CgBoostAssetFormat;
  platforms: CgBoostPlatform[];
  confidence: 'high' | 'medium';
}

export interface MomentumWeekPoint {
  weekIndex: number;
  weekLabel: string;
  impressions: number;
  views: number;
  spend: number;
  vtr: number;
  ctr: number;
  frequency: number;
}

export interface MomentumPayload {
  campaign: CgBoostCampaign;
  weekly: MomentumWeekPoint[];
  optimizationNotes: Array<{ date: string; note: string }>;
  flags: {
    fatigueDetected: boolean;
    momentumMaintained: boolean;
    refreshWindow: boolean;
    frequencyWarning: boolean;
    plainLanguage: { fatigue: string; momentum: string; refresh: string; frequency: string };
  };
}

export interface ContentResonanceEntry {
  canonicalAssetId: string;
  creatorName: string;
  format: CgBoostAssetFormat;
  hookStyle: string;
  score: number;
  rank: number;
  flag: 'top' | 'hookStrength' | null;
  plainLanguage: string;
}

export interface PlatformSignalEntry {
  platform: CgBoostPlatform;
  value: number;
  flag: 'strongestResponse' | 'mostEfficient' | 'recommended' | null;
  plainLanguage: string;
}

export interface CreativeFatigueEntry {
  canonicalAssetId: string;
  creatorName: string;
  format: CgBoostAssetFormat;
  week1Ctr: number;
  week2Ctr: number;
  pctChange: number;
  flag: 'fatigue' | 'momentum' | null;
  plainLanguage: string;
}

export interface SignalsPayload {
  campaign: CgBoostCampaign;
  computedAt: string;
  contentResonance: ContentResonanceEntry[];
  audienceActionRate: PlatformSignalEntry[];
  reachEfficiency: PlatformSignalEntry[];
  creativeFatigue: CreativeFatigueEntry[];
  refreshWindow: { weeklySpendUsd: number; triggered: boolean; plainLanguage: string };
  frequencyWarnings: Array<{
    platform: CgBoostPlatform;
    frequency: number;
    threshold: number;
    triggered: boolean;
    plainLanguage: string;
  }>;
  platformValueScore: PlatformSignalEntry[];
}

export interface BenchmarkComparisonRow {
  metric: 'cpm' | 'vtr' | 'hookRate' | 'ctr';
  current: number;
  historicalAvg: number;
  deltaPct: number;
  direction: 'better' | 'worse' | 'flat';
}

export interface CrossCampaignPattern {
  label: string;
  appearances: number;
  averageScore: number;
}

export interface IntelligencePayload {
  campaign: CgBoostCampaign;
  benchmarkComparison: BenchmarkComparisonRow[];
  topFormats: CrossCampaignPattern[];
  topCreators: CrossCampaignPattern[];
  topHookStyles: CrossCampaignPattern[];
  briefRecommendation: string;
}

export interface CreateCampaignRequest {
  brandName: string;
  campaignName?: string;
  industryVertical: string;
  startDate: string;
  endDate: string;
  firstLineOfCopy: string;
  creators: Array<{ name: string; airtableRecordId?: string }>;
  plannedBudget: { tiktok?: number; meta?: number; youtube?: number };
  platformIds?: CgBoostPlatformIds;
}
